import React from 'react';
import {
  JobCard,
  JobCardProps,
  Benefit,
  JobType,
  isSalarySource,
  Enhancement,
  RemoteVariant,
  isQualificationGrade,
} from '@zr/zds/src/components/JobCard';
import { ButtonGroup } from '@zr/zds/src/components/ButtonGroup';
import { JobResult } from './types.d';
import { ApplyButton, ApplyButtonProps } from './ApplyButton';
import { SaveJobButton } from './SaveJobButton';
import { ExternalFeedback } from './ExternalFeedback';

export type JobResultRowProps = {
  job: JobResult;
  placementId: string;
  impressionSuperSetId: string;
  isEligibleForOneClickApply?: boolean;
};

function transformEnhancements(job: JobResult): Array<Enhancement> {
  // mapping from SEO EmploymentTags to JobCard Enhancement
  const enhancementsMap: Record<string, Enhancement> = {
    willing_to_train: 'Training provided',
    relocation_assistance: 'Relocation assistance',
    urgent: 'Urgent',
    veteran_friendly: 'Veteran-friendly',
    signing_bonus: 'Signing bonus',
  };
  return Object.entries(job.EmploymentTags).reduce((enhancements, [tag, value]): Array<Enhancement> => {
    const enhancement = enhancementsMap[tag];
    if (enhancement && value) {
      enhancements.push(enhancement);
    }
    return enhancements;
  }, new Array<Enhancement>());
}

function transformRemoteVariant(job: JobResult): RemoteVariant {
  // currently we don't have support for 'Hybrid' so this is just temporary until that gets added
  if (job.EmploymentTags.remote) {
    return 'Remote';
  }
  return 'In-person';
}

function transformBenefits(job: JobResult): Array<Benefit> {
  // mapping from SEO Benefits to JobCard Benefits
  const benefitsMap: Record<string, Benefit> = {
    Vision: 'Vision',
    Medical: 'Medical',
    'Life Insurance': 'Life',
    '401k': '401k',
    Dental: 'Dental',
  };
  return (job.Benefits || []).reduce((benefits, b) => {
    const benefit = benefitsMap[b];
    if (benefit) {
      benefits.push(benefit);
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Skipping Benefit "${b}" because it was not found in the benefitsMap!`);
    }
    return benefits;
  }, new Array<Benefit>());
}

function transformJobType(job: JobResult): JobType {
  // mapping from SEO EmploymentType to JobCard JobType
  const jobTypesMap: Record<string, JobType> = {
    'Full-Time': 'Full-time',
    'Part-Time': 'Part-time',
    Contractor: 'Contract',
    Temporary: 'Temporary',
    // Interim: '?',
    // Internship: '?',
    // Summer: '?',
    'Per Diem': 'Per diem',
    // Nights: '?',
    Other: 'Other',
    // '?': 'Contract to hire',
  };
  const jobType = jobTypesMap[job.EmploymentType];
  if (!jobType) {
    // eslint-disable-next-line no-console
    console.warn(`Skipping EmploymentType "${job.EmploymentType}" because it was not found in the jobTypesMap!`);
  }
  return jobType;
}

function transformAppliedStatus(job: JobResult): ApplyButtonProps['appliedStatus'] {
  const appliedStatusMap = new Map<number, ApplyButtonProps['appliedStatus']>([
    [0, 'in_progress'],
    [1, 'delivered'],
    [2, 'not_started'],
  ]);
  if (appliedStatusMap.has(job.AppliedStatus)) {
    const status = appliedStatusMap.get(job.AppliedStatus);
    if (status) {
      return status;
    }
  }
  return 'not_started';
}

function transformJob(job: JobResult): JobCardProps {
  return {
    title: job.Title,
    companyLogoUrl: job.CompanyLogoURL ? job.CompanyLogoURL : undefined,
    companyName: job.OrgName,
    companyUrl: job.OrgURL,
    location: `${job.City}, ${job.State}`,
    locationUrl: job.LocationURL,
    enhancements: transformEnhancements(job),
    remoteVariant: transformRemoteVariant(job),
    formattedSalary: job.ShowSalary ? job.FormattedSalary : undefined,
    salarySource: isSalarySource(job.SalarySource) ? job.SalarySource : undefined,
    benefits: transformBenefits(job),
    jobType: transformJobType(job),
    snippet: job.Snippet,
    href: job.Href,
    qualificationGrade: isQualificationGrade(job.GreatMatchScore.qualification_grade)
      ? job.GreatMatchScore.qualification_grade
      : undefined,
    isNew: job.IsNewJob,
  };
}

export const JobResultRow = ({
  job,
  placementId,
  impressionSuperSetId,
  isEligibleForOneClickApply,
}: JobResultRowProps): JSX.Element => {
  const [showExternalFeedback, setShowExternalFeedback] = React.useState(false);

  const onExternalApply = () => {
    setShowExternalFeedback(true);
  };

  return (
    <div className="job_result" key={job.QuizID}>
      <JobCard
        {...transformJob(job)}
        renderActionBar={() => (
          <ButtonGroup fullWidth>
            <ApplyButton
              encryptedQuizId={job.EncryptedQuizID}
              listingVersionKey={job.LVK}
              placementId={placementId}
              appliedStatus={transformAppliedStatus(job)}
              intentToApplyUrl={job.Href}
              jobTitle={job.Title}
              jobApplyUrl={job.JobURL}
              jobPageUrl={job.Href}
              isOneClickApply={isEligibleForOneClickApply}
              isExternal={job.IsExternal}
              openInTab={job.OpenInTab}
              onExternalApply={onExternalApply}
            />
            <SaveJobButton
              saveJobUrl={job.SaveJobURL}
              placementId={placementId}
              impressionSuperSetId={impressionSuperSetId}
              isJobSaved={job.IsJobSaved}
            />
          </ButtonGroup>
        )}
      />
      {showExternalFeedback && <ExternalFeedback listingVersionKey={job.LVK} placementId={placementId} />}
    </div>
  );
};
